/**
 * Jauge de santé semi-circulaire compacte (réutilisée de la synthèse fiche
 * client, version réduite pour ne pas être prégnante). Colorée au seuil :
 * ≥70 success / 40-69 warning / <40 destructive. Rendu Canvas-free (polyline SVG).
 */

import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { SummaryGauge as GaugeData } from '../types'

const R = 40
const CX = 50
const CY = 50
const N = 48

function arcPoints(count: number): string {
    const pts: string[] = []
    for (let i = 0; i <= count; i++) {
        const a = Math.PI - (i / N) * Math.PI
        pts.push(`${(CX + R * Math.cos(a)).toFixed(1)},${(CY - R * Math.sin(a)).toFixed(1)}`)
    }
    return pts.join(' ')
}

export function SummaryGauge({ gauge }: { gauge: GaugeData }) {
    const { t } = useTranslation()
    const score = Math.max(0, Math.min(100, gauge.score))
    const tone = score >= 70 ? 'success' : score >= 40 ? 'warning' : 'destructive'
    const strokeClass =
        tone === 'success'
            ? 'stroke-success'
            : tone === 'warning'
              ? 'stroke-warning'
              : 'stroke-destructive'
    const numClass =
        tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : 'text-destructive'
    const k = Math.max(1, Math.round((N * score) / 100))

    return (
        <div className="flex items-center gap-4">
            {/* viewBox 0..64 en hauteur → marge sous la ligne de base (y=50) pour
                loger le nombre sans qu'il touche l'arc. Trait fin (7) plus élégant. */}
            <div className="relative h-[4.5rem] w-32 shrink-0">
                <svg viewBox="0 0 100 64" className="h-full w-full" aria-hidden="true">
                    <polyline
                        points={arcPoints(N)}
                        className="fill-none stroke-muted"
                        strokeWidth={7}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <polyline
                        points={arcPoints(k)}
                        className={cn('fill-none', strokeClass)}
                        strokeWidth={7}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                {/* Nombre logé dans l'ouverture basse du dôme (centré, aéré). */}
                <div className="absolute inset-x-0 bottom-1 flex flex-col items-center leading-none">
                    <span className={cn('text-2xl font-bold tabular-nums', numClass)}>{score}</span>
                    <span className="mt-1 text-[11px] text-muted-foreground">/ 100</span>
                </div>
            </div>
            {gauge.reliability && (
                <span className="inline-flex items-center gap-1 self-start rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                    <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                    {t(gauge.reliability, gauge.reliability)}
                </span>
            )}
        </div>
    )
}
